import { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ChartBar,
  ChartLine,
  Database,
  Download,
  FlowArrow,
  Keyboard,
  Sparkle,
} from '@phosphor-icons/react';

interface WorkflowShortcutsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  selectedDatasetCount: number;
  hasQualityReports: boolean;
  hasCorrelations: boolean;
  onShowKeyboardHelp?: () => void;
}

const workflowSteps = [
  {
    id: 'templates',
    label: 'Start',
    icon: Sparkle,
    shortcut: '1',
    description: 'Choose template or browse sources',
  },
  {
    id: 'browse-all',
    label: 'Select Data',
    icon: Database,
    shortcut: '2',
    description: 'Pick datasets for analysis',
  },
  {
    id: 'quality',
    label: 'Quality',
    icon: ChartLine,
    shortcut: '3',
    description: 'Check data quality reports',
  },
  {
    id: 'correlations',
    label: 'Analyze',
    icon: ChartLine,
    shortcut: '4',
    description: 'Find correlations between datasets',
  },
  {
    id: 'transform',
    label: 'Transform',
    icon: FlowArrow,
    shortcut: '5',
    description: 'Build data transformation pipelines',
  },
  {
    id: 'visualize',
    label: 'Visualize',
    icon: ChartBar,
    shortcut: '6',
    description: 'Create interactive charts',
  },
  {
    id: 'export',
    label: 'Export',
    icon: Download,
    shortcut: '7',
    description: 'Download data in various formats',
  },
];

export function WorkflowShortcuts({
  activeTab,
  onTabChange,
  selectedDatasetCount,
  hasQualityReports,
  hasCorrelations,
  onShowKeyboardHelp,
}: WorkflowShortcutsProps) {
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in inputs
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Check for number keys 1-7
      const shortcutMap: Record<string, string> = {
        '1': 'templates',
        '2': 'browse-all',
        '3': 'quality',
        '4': 'correlations',
        '5': 'transform',
        '6': 'visualize',
        '7': 'export',
      };

      if (shortcutMap[event.key]) {
        event.preventDefault();

        // Check if step is available
        const targetTab = shortcutMap[event.key];

        if (isStepAvailable(targetTab)) {
          onTabChange(targetTab);

          // Show visual feedback for keyboard navigation
          const stepElement = document.querySelector(
            `[data-step="${targetTab}"]`
          );

          if (stepElement) {
            stepElement.classList.add('animate-pulse');
            setTimeout(() => {
              stepElement.classList.remove('animate-pulse');
            }, 600);
          }
        }
      }

      // Handle arrow key navigation
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const currentIndex = workflowSteps.findIndex(
          step => step.id === activeTab
        );

        if (currentIndex !== -1) {
          const direction = event.key === 'ArrowLeft' ? -1 : 1;
          let newIndex = currentIndex + direction;

          // Find next available step
          while (newIndex >= 0 && newIndex < workflowSteps.length) {
            const targetStep = workflowSteps[newIndex];

            if (isStepAvailable(targetStep.id)) {
              onTabChange(targetStep.id);
              break;
            }
            newIndex += direction;
          }
        }
      }

      // Show keyboard help with '?' key
      if (event.key === '?' && onShowKeyboardHelp) {
        event.preventDefault();
        onShowKeyboardHelp();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    onTabChange,
    selectedDatasetCount,
    hasQualityReports,
    hasCorrelations,
    activeTab,
    onShowKeyboardHelp,
  ]);

  const isStepAvailable = (stepId: string) => {
    switch (stepId) {
      case 'templates':
      case 'browse-all':
        return true;
      case 'quality':
      case 'transform':
      case 'visualize':
      case 'export':
        return selectedDatasetCount > 0;
      case 'correlations':
        return selectedDatasetCount >= 2;
      default:
        return false;
    }
  };

  const getStepStatus = (stepId: string) => {
    if (stepId === activeTab) {
      return 'active';
    }
    if (!isStepAvailable(stepId)) {
      return 'disabled';
    }

    // Check completion status
    switch (stepId) {
      case 'templates':
        return 'completed'; // Always available
      case 'browse-all':
        return selectedDatasetCount > 0 ? 'completed' : 'available';
      case 'quality':
        return hasQualityReports ? 'completed' : 'available';
      case 'correlations':
        return hasCorrelations ? 'completed' : 'available';
      case 'transform':
      case 'visualize':
      case 'export':
        return 'available';
      default:
        return 'available';
    }
  };

  const getCompletionCount = () => {
    let completed = 1; // Start step always completed

    if (selectedDatasetCount > 0) {
      completed++;
    }
    if (hasQualityReports) {
      completed++;
    }
    if (hasCorrelations) {
      completed++;
    }

    return completed;
  };

  return (
    <Card className='mb-6'>
      <CardContent className='p-3'>
        {/* Compact Header */}
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <h3 className='text-sm font-medium text-muted-foreground'>
              Data Science Workflow
            </h3>
            <Badge variant='outline' className='text-xs hidden sm:inline-flex'>
              1-7 keys
            </Badge>
          </div>
          <div className='text-xs text-muted-foreground'>
            {getCompletionCount()}/{workflowSteps.length}
          </div>
        </div>

        {/* Responsive Step Navigation */}
        <div className='grid grid-cols-2 sm:grid-cols-7 gap-1 sm:gap-2 mb-3'>
          {workflowSteps.map((step, index) => {
            const Icon = step.icon;
            const status = getStepStatus(step.id);
            const isActive = step.id === activeTab;
            const isAvailable = isStepAvailable(step.id);

            return (
              <button
                key={step.id}
                onClick={() => isAvailable && onTabChange(step.id)}
                disabled={!isAvailable}
                data-step={step.id}
                className={`
                  group relative flex flex-col items-center gap-1 p-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary min-w-0
                  ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : isAvailable
                        ? 'hover:bg-muted/50 text-foreground'
                        : 'text-muted-foreground cursor-not-allowed opacity-50'
                  }
                  ${isAvailable ? 'cursor-pointer' : ''}
                `}
                title={`${step.description}${isAvailable ? ` (Press ${step.shortcut})` : ''}`}
                tabIndex={isAvailable ? 0 : -1}
              >
                {/* Step Icon */}
                <div
                  className={`
                  relative flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full flex-shrink-0
                  ${
                    isActive
                      ? 'bg-primary-foreground/20'
                      : status === 'completed'
                        ? 'bg-quality-good/20'
                        : 'bg-muted/50'
                  }
                `}
                >
                  <Icon className='w-3 h-3 sm:w-3.5 sm:h-3.5' />

                  {/* Completion indicator */}
                  {status === 'completed' && !isActive && (
                    <div className='absolute -top-0.5 -right-0.5 w-2 h-2 bg-quality-good rounded-full'>
                      <div className='w-1 h-1 bg-white rounded-full m-0.5' />
                    </div>
                  )}
                </div>

                {/* Step Label */}
                <div className='text-center min-w-0 max-w-full'>
                  <div className='text-xs font-medium truncate'>
                    {step.label}
                  </div>
                  <div
                    className={`
                    text-xs mt-0.5 px-1 py-0.5 rounded border
                    ${
                      isActive
                        ? 'border-primary-foreground/20 bg-primary-foreground/10'
                        : 'border-muted bg-muted/30'
                    }
                  `}
                  >
                    {step.shortcut}
                  </div>
                </div>

                {/* Tooltip on hover - only on larger screens */}
                <div className='absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 hidden sm:block'>
                  <div className='bg-popover text-popover-foreground text-xs px-2 py-1 rounded border whitespace-nowrap'>
                    {step.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Compact Progress Bar */}
        <div className='w-full bg-muted rounded-full h-1.5 mb-2'>
          <div
            className='bg-primary h-1.5 rounded-full transition-all duration-500'
            style={{
              width: `${(getCompletionCount() / workflowSteps.length) * 100}%`,
            }}
          />
        </div>

        {/* Current Step Description - More Compact */}
        <div className='text-center'>
          <div className='text-xs text-muted-foreground'>
            {workflowSteps.find(step => step.id === activeTab)?.description ||
              'Navigate workflow'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
